import React, { useState, useEffect, useCallback } from 'react';
import { medicPortalAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Calendar } from '../../components/ui/calendar';
import { Textarea } from '../../components/ui/textarea';
import { 
  CalendarDays, 
  Check,
  X,
  Loader2,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, startOfMonth, endOfMonth, addMonths } from 'date-fns';

export default function MedicAvailability() {
  const [selectedDates, setSelectedDates] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const fetchAvailability = useCallback(async () => {
    try {
      const startDate = format(startOfMonth(addMonths(currentMonth, -1)), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(addMonths(currentMonth, 2)), 'yyyy-MM-dd');
      
      const response = await medicPortalAPI.getAvailability({ 
        start_date: startDate, 
        end_date: endDate 
      });
      setAvailability(response.data);
    } catch (error) {
      // If no availability data, that's fine
      setAvailability([]);
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const handleDateSelect = (dates) => {
    setSelectedDates(dates || []);
  };

  const handleSetAvailability = async (isAvailable) => {
    if (selectedDates.length === 0) {
      toast.error('Please select at least one date');
      return;
    }

    setSaving(true);
    try {
      const dateStrings = selectedDates.map(d => format(d, 'yyyy-MM-dd'));
      
      // Set availability for each selected date
      for (const date of dateStrings) {
        await medicPortalAPI.setAvailability({
          date,
          available: isAvailable,
          notes: notes || undefined,
        });
      }
      
      toast.success(`Set ${dateStrings.length} date(s) as ${isAvailable ? 'available' : 'unavailable'}`);
      setSelectedDates([]);
      setNotes('');
      fetchAvailability();
    } catch (error) {
      toast.error('Failed to update availability');
    } finally {
      setSaving(false);
    }
  };

  const getModifiers = useCallback(() => {
    const available = [];
    const unavailable = [];
    
    availability.forEach(item => {
      try {
        const parsedDate = parseISO(item.date);
        if (item.available) {
          available.push(parsedDate);
        } else {
          unavailable.push(parsedDate);
        }
      } catch (e) {
        // Skip invalid dates
      }
    });

    return { available, unavailable };
  }, [availability]);

  // Get upcoming availability summary
  const getUpcomingSummary = () => {
    const today = new Date();
    const upcoming = availability.filter(a => new Date(a.date) >= today);
    const availableCount = upcoming.filter(a => a.available).length;
    const unavailableCount = upcoming.filter(a => !a.available).length;
    return { availableCount, unavailableCount };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  const modifiers = getModifiers();
  const summary = getUpcomingSummary();

  return (
    <div className="space-y-6" data-testid="medic-availability-container">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <Check className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Available Days</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {summary.availableCount}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/15">
                <X className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Unavailable Days</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {summary.unavailableCount}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#FFC04D]/15">
                <CalendarDays className="w-5 h-5 text-[#FFC04D]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Selected</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {selectedDates.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Card */}
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardHeader>
          <CardTitle className="text-[#F2F2F2] font-['Manrope'] flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#1ABC9C]" />
            Set Your Availability
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Calendar */}
            <div className="flex flex-col items-center">
              <Calendar
                mode="multiple"
                selected={selectedDates}
                onSelect={handleDateSelect}
                month={currentMonth}
                onMonthChange={setCurrentMonth}
                className="rounded-md border border-white/10 bg-[#1A1A1A] p-3"
                modifiers={modifiers}
                modifiersStyles={{
                  available: {
                    backgroundColor: 'rgba(26, 188, 156, 0.2)',
                    color: '#1ABC9C',
                    borderRadius: '4px',
                  },
                  unavailable: {
                    backgroundColor: 'rgba(239, 68, 68, 0.2)',
                    color: '#ef4444',
                    borderRadius: '4px',
                  },
                }}
                data-testid="availability-calendar"
              />
              
              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-4 p-3 bg-[#1A1A1A] rounded-lg border border-white/5 w-full max-w-xs">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-[#1ABC9C]/20 border border-[#1ABC9C]/30" />
                  <span className="text-xs text-[#A1A1AA]">Available</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-500/20 border border-red-500/30" />
                  <span className="text-xs text-[#A1A1AA]">Unavailable</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-blue-500/50 border border-blue-500" />
                  <span className="text-xs text-[#A1A1AA]">Selected</span>
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="space-y-6">
              {/* Selected Dates */}
              <div>
                <h3 className="text-lg font-semibold text-[#F2F2F2] mb-3">
                  Selected Dates
                </h3>
                
                {selectedDates.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mb-4 max-h-28 overflow-y-auto p-3 bg-[#1A1A1A] rounded-lg border border-white/5">
                    {selectedDates.sort((a, b) => a - b).map(date => (
                      <Badge 
                        key={date.toISOString()}
                        variant="outline"
                        className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]"
                      >
                        {format(date, 'MMM dd')}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5 mb-4">
                    <p className="text-sm text-[#A1A1AA] flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      Click on dates in the calendar to select them
                    </p>
                  </div>
                )}

                {/* Notes */}
                <div className="mb-4">
                  <label className="text-sm text-[#A1A1AA] mb-2 block">
                    Notes (optional)
                  </label>
                  <Textarea
                    placeholder="Add any notes for the scheduler..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="bg-[#1A1A1A] border-white/10 text-[#F2F2F2] placeholder:text-[#A1A1AA] resize-none"
                    rows={2}
                    data-testid="availability-notes"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleSetAvailability(true)}
                    disabled={saving || selectedDates.length === 0}
                    className="flex-1 bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                    data-testid="set-available-btn"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Set Available
                  </Button>
                  <Button
                    onClick={() => handleSetAvailability(false)}
                    disabled={saving || selectedDates.length === 0}
                    variant="destructive"
                    className="flex-1"
                    data-testid="set-unavailable-btn"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <X className="w-4 h-4 mr-2" />
                    )}
                    Set Unavailable
                  </Button>
                </div>
              </div>

              {/* Instructions */}
              <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
                <h4 className="text-sm font-medium text-[#F2F2F2] mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4 text-[#1ABC9C]" />
                  How it works
                </h4>
                <ul className="text-sm text-[#A1A1AA] space-y-1 list-disc list-inside">
                  <li>Click dates to select them (multi-select enabled)</li>
                  <li>Add optional notes for schedulers</li>
                  <li>Click "Set Available" or "Set Unavailable"</li>
                  <li>Schedulers will see your availability when assigning shifts</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
